import React from 'react';
import { MessageSquare, Star } from 'lucide-react';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER } from '../../../util/types';

import { Card, CardContent } from '../../../components/ui/card';

import { MOCK_PROVIDER_REVIEWS } from './mockReviews';

export const REVIEWS_SECTION_ANCHOR_ID = 'seller-reviews';

type ProfileReviewsSectionProps = {
  reviews: any[];
  queryReviewsError?: any;
  /** When the URL has `?previewMockReviews=1`, render the mock data shape for design preview. */
  previewMockReviews?: boolean;
};

/**
 * Renders reviews left for this user as a provider (i.e. seller-side
 * feedback). Customer-side reviews are intentionally omitted from the public
 * profile per design: only the seller's track record is shown.
 *
 * Empty state (no provider reviews) renders an explanatory placeholder card.
 * Pass `previewMockReviews` to swap in Sharetribe-shaped mock data: handy
 * for design QA before the marketplace has any real reviews.
 */
const ProfileReviewsSection = ({
  reviews,
  queryReviewsError,
  previewMockReviews = false,
}: ProfileReviewsSectionProps) => {
  const intl = useIntl();

  const realProviderReviews = reviews.filter(
    r => r?.attributes?.type === REVIEW_TYPE_OF_PROVIDER
  );
  const providerReviews =
    realProviderReviews.length > 0
      ? realProviderReviews
      : previewMockReviews
      ? MOCK_PROVIDER_REVIEWS
      : [];
  const isEmpty = providerReviews.length === 0;

  if (queryReviewsError) {
    return (
      <section
        id={REVIEWS_SECTION_ANCHOR_ID}
        className="space-y-3 scroll-mt-24"
      >
        <h2 className="text-2xl font-heading font-bold uppercase tracking-tight">
          <FormattedMessage id="CustomProfilePage.reviewsTitle" />
        </h2>
        <p className="text-sm text-destructive">
          <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
        </p>
      </section>
    );
  }

  if (isEmpty) {
    return (
      <section
        id={REVIEWS_SECTION_ANCHOR_ID}
        className="space-y-3 scroll-mt-24"
      >
        <div>
          <h2 className="text-2xl font-heading font-bold uppercase tracking-tight">
            <FormattedMessage id="CustomProfilePage.reviewsTitle" />
          </h2>
          <p className="text-sm text-muted-foreground">
            <FormattedMessage id="CustomProfilePage.reviewsSubtitle" />
          </p>
        </div>
        <Card
          className="border-dashed border-border/80 bg-white/60 shadow-none"
          data-testid="card-public-reviews-empty"
        >
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <MessageSquare className="h-6 w-6 text-primary" />
            </div>
            <p className="font-heading text-lg font-bold uppercase tracking-tight">
              <FormattedMessage id="CustomProfilePage.reviewsEmptyHeading" />
            </p>
            <p className="max-w-md text-sm text-muted-foreground">
              <FormattedMessage id="CustomProfilePage.reviewsEmptyBody" />
            </p>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section
      id={REVIEWS_SECTION_ANCHOR_ID}
      className="space-y-3 scroll-mt-24"
    >
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold uppercase tracking-tight">
            <FormattedMessage id="CustomProfilePage.reviewsTitle" />
          </h2>
          <p className="text-sm text-muted-foreground">
            <FormattedMessage id="CustomProfilePage.reviewsSubtitle" />
          </p>
        </div>
        <div
          className="rounded-full border border-border bg-white px-3 py-1 text-xs font-heading uppercase tracking-[0.18em] text-muted-foreground"
          data-testid="text-public-profile-review-summary"
        >
          <FormattedMessage
            id="CustomProfilePage.reviewsCount"
            values={{ count: providerReviews.length }}
          />
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {providerReviews.map(review => {
          const reviewId = review?.id?.uuid;
          const authorProfile = review?.author?.attributes?.profile || {};
          const authorName =
            authorProfile.displayName || authorProfile.abbreviatedName || 'Buyer';
          const rating = review?.attributes?.rating ?? null;
          const content = review?.attributes?.content ?? '';
          const createdAt = review?.attributes?.createdAt;
          const dateString = createdAt
            ? intl.formatDate(new Date(createdAt), { month: 'long', year: 'numeric' })
            : '';

          return (
            <Card
              key={reviewId}
              className="border-border/80 bg-white shadow-sm"
              data-testid={`card-public-review-${reviewId}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p
                      className="font-semibold text-foreground"
                      data-testid={`text-public-review-author-${reviewId}`}
                    >
                      {authorName}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <FormattedMessage id="CustomProfilePage.reviewerRole" />
                    </p>
                  </div>
                  {rating != null ? (
                    <div className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-foreground">
                      <Star className="h-3.5 w-3.5 fill-primary text-primary" />
                      {Number(rating).toFixed(1)}
                    </div>
                  ) : null}
                </div>
                <p
                  className="mt-3 text-sm leading-6 text-muted-foreground"
                  data-testid={`text-public-review-body-${reviewId}`}
                >
                  {content}
                </p>
                {dateString ? (
                  <p className="mt-3 text-[11px] font-heading uppercase tracking-[0.16em] text-muted-foreground">
                    {dateString}
                  </p>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </section>
  );
};

export default ProfileReviewsSection;
